import { z } from 'zod';

// Wire contract for the in-app notification bell.
// Mirrors the `notifications` pg schema (packages/db/src/schema/notifications.ts).
//
// No client-facing trigger: every event is raised server-side by the domain
// that owns it (`traveller_preview_opened` from preview; the customer
// Finish-review CTAs from customer-preview's public appendMessage;
// `support_ticket_commented` from support when somebody comments on a ticket;
// `operational_itinerary_cancelled` from itineraries when an itinerary on an
// operational status is cancelled).
// The wire surface is the inbox only.

export const notificationTypeValues = [
  'traveller_preview_opened',
  'customer_needs_review',
  'customer_ready_for_client',
  'support_ticket_commented',
  'operational_itinerary_cancelled',
] as const;
export const notificationTypeSchema = z.enum(notificationTypeValues);
export type NotificationType = z.infer<typeof notificationTypeSchema>;

export const notificationEntityTypeValues = [
  'itinerary',
  'support_ticket',
] as const;
export const notificationEntityTypeSchema = z.enum(
  notificationEntityTypeValues
);
export type NotificationEntityType = z.infer<
  typeof notificationEntityTypeSchema
>;

export const notificationSchema = z.object({
  id: z.uuid(),
  type: notificationTypeSchema,
  title: z.string(),
  body: z.string().nullable(),
  // Both present or both null — enforced in the DB by chk_notification_entity_ref.
  entityType: notificationEntityTypeSchema.nullable(),
  entityId: z.uuid().nullable(),
  createdOn: z.string(),
  readOn: z.string().nullable(),
});
export type Notification = z.infer<typeof notificationSchema>;

// Keyset (not offset) pagination: the bell list is an append-at-the-top feed,
// so an offset page-2 would skip or repeat rows whenever a notification arrives
// between requests. `(createdOn, id)` is the full sort key — id breaks ties on
// same-millisecond rows so the order is total and the scan deterministic.
export const notificationCursorSchema = z.object({
  createdOn: z.string(),
  id: z.uuid(),
});
export type NotificationCursor = z.infer<typeof notificationCursorSchema>;

export const findNotificationsInput = z.object({
  /** Size of the FIRST page. */
  limit: z.number().int().min(1).max(50).default(20),
  /**
   * Size of every page after the first. Optional; falls back to `limit`.
   *
   * It lives on the input rather than on the cursor because TanStack Query
   * varies only the page param between pages — so a per-page size has to be
   * part of the (constant) input, where it also keeps the query key stable and
   * therefore costs no extra refetch.
   */
  nextLimit: z.number().int().min(1).max(50).optional(),
  // Named `cursor` because TanStack Query's `useInfiniteQuery` writes the page
  // param into exactly this field.
  cursor: notificationCursorSchema.nullish(),
});
export type FindNotificationsInput = z.infer<typeof findNotificationsInput>;

export const listNotificationsResult = z.object({
  data: z.array(notificationSchema),
  nextCursor: notificationCursorSchema.nullable(),
});
export type ListNotificationsResult = z.infer<typeof listNotificationsResult>;

export const markNotificationReadInput = z.object({ id: z.uuid() });
export type MarkNotificationReadInput = z.infer<
  typeof markNotificationReadInput
>;

export const markAllNotificationsReadResult = z.object({
  updated: z.number().int(),
});
export type MarkAllNotificationsReadResult = z.infer<
  typeof markAllNotificationsReadResult
>;

// XT-782 — the admin "send the follow-up now" test action. Takes the itinerary
// rather than an outbox row id: the point of the button is to exercise the
// whole path (resolve the customer, render the org's template, hand it to the
// mail provider) for a trip the admin is looking at, which is exactly what a
// row id would let you skip.
export const sendPreviewFollowUpNowInput = z.object({
  itineraryId: z.uuid(),
  // Optional test recipient. An admin checking the wording should not have to
  // email a real travel agent to see it, so they can redirect the test send to
  // their own address; omitted, it goes to the trip's actual customer.
  toEmail: z.email().nullish(),
});
export type SendPreviewFollowUpNowInput = z.infer<
  typeof sendPreviewFollowUpNowInput
>;

export const sendPreviewFollowUpNowResult = z.object({
  sent: z.boolean(),
  // The address it went to, echoed back so the admin can confirm the test
  // landed where they expected without opening the CRM record.
  toEmail: z.string().nullable(),
  // Why nothing was sent, when `sent` is false. A test button that silently
  // does nothing is worse than no button, so every refusal has a reason the UI
  // can show verbatim.
  reason: z.string().nullable(),
});
export type SendPreviewFollowUpNowResult = z.infer<
  typeof sendPreviewFollowUpNowResult
>;
